import { createSlice } from '@reduxjs/toolkit';
import { FEATURE_NAME } from './constants';
import type { HostOrganizationSliceState } from './types';
import {
  checkOrganizationStatus,
  fetchMyOrganization,
  fetchOrganizationById,
  createOrganization,
  updateOrganization,
  fetchMembers,
  inviteMember,
  removeMember,
  resendInvitation,
  deletePendingInvitation,
} from './actions';

const initialState: HostOrganizationSliceState = {
  currentOrganization: null,
  eventHostOrganization: null,
  organizationStatus: null,
  members: [],
  isLoading: false,
  error: null,
  statusLoading: false,
  statusError: null,
  membersLoading: false,
  membersError: null,
  createLoading: false,
  createError: null,
  updateLoading: false,
  updateError: null,
  inviteLoading: false,
  inviteError: null,
  fetchOrgByIdLoading: false,
  fetchOrgByIdError: null,
};

export const hostOrganizationSlice = createSlice({
  name: FEATURE_NAME,
  initialState,
  reducers: {
    resetCreateError: (state) => {
      state.createError = null;
    },
    resetUpdateError: (state) => {
      state.updateError = null;
    },
    resetInviteError: (state) => {
      state.inviteError = null;
    },
    clearOrganizationStatus: (state) => {
      state.organizationStatus = null;
      state.statusError = null;
    },
  },
  extraReducers: (builder) => {
    // Check organization status
    builder.addCase(checkOrganizationStatus.pending, (state) => {
      state.statusLoading = true;
      state.statusError = null;
    });
    builder.addCase(checkOrganizationStatus.fulfilled, (state, action) => {
      state.statusLoading = false;
      state.organizationStatus = action.payload.data;
      // Store the organization if it exists in the response
      if (action.payload.data?.organization) {
        state.currentOrganization = action.payload.data.organization;
      }
    });
    builder.addCase(checkOrganizationStatus.rejected, (state, action) => {
      state.statusLoading = false;
      state.statusError = action.error.message || 'Failed to check organization status';
    });

    // Fetch my organization
    builder.addCase(fetchMyOrganization.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchMyOrganization.fulfilled, (state, action) => {
      state.isLoading = false;
      // API returns { success, has_organization, organization }
      state.currentOrganization = action.payload.data?.organization || action.payload.data;
    });
    builder.addCase(fetchMyOrganization.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || 'Failed to fetch organization';
    });

    // Create organization
    builder.addCase(createOrganization.pending, (state) => {
      state.createLoading = true;
      state.createError = null;
    });
    builder.addCase(createOrganization.fulfilled, (state, action) => {
      state.createLoading = false;
      // API returns { success, organization } or just the organization
      const organization = action.payload.data?.organization || action.payload.data;
      state.currentOrganization = organization;
      state.organizationStatus = {
        has_organization: true,
        should_prompt: false,
        organization_id: organization.id,
      };
    });
    builder.addCase(createOrganization.rejected, (state, action) => {
      state.createLoading = false;
      state.createError = action.error.message || 'Failed to create organization';
    });

    // Update organization
    builder.addCase(updateOrganization.pending, (state) => {
      state.updateLoading = true;
      state.updateError = null;
    });
    builder.addCase(updateOrganization.fulfilled, (state, action) => {
      state.updateLoading = false;
      // API returns { success, organization } or just the organization
      state.currentOrganization = action.payload.data?.organization || action.payload.data;
    });
    builder.addCase(updateOrganization.rejected, (state, action) => {
      state.updateLoading = false;
      state.updateError = action.error.message || 'Failed to update organization';
    });

    // Fetch members
    builder.addCase(fetchMembers.pending, (state) => {
      state.membersLoading = true;
      state.membersError = null;
    });
    builder.addCase(fetchMembers.fulfilled, (state, action) => {
      state.membersLoading = false;
      // API returns { success, members, count }
      state.members = action.payload.data?.members || action.payload.data || [];
    });
    builder.addCase(fetchMembers.rejected, (state, action) => {
      state.membersLoading = false;
      state.membersError = action.error.message || 'Failed to fetch members';
    });

    // Invite member
    builder.addCase(inviteMember.pending, (state) => {
      state.inviteLoading = true;
      state.inviteError = null;
    });
    builder.addCase(inviteMember.fulfilled, (state, action) => {
      state.inviteLoading = false;
      // API returns { success, message, invitation }
      const invitation = action.payload.data?.invitation || action.payload.data;

      // Map invitation to member format
      const newMember = {
        id: invitation.id,
        account_id: '', // Not available in invitation
        email: invitation.email,
        first_name: undefined,
        last_name: undefined,
        avatar_url: null,
        role: invitation.role?.toLowerCase() || 'manager',
        is_active: false,
        invitation_status: invitation.status?.toLowerCase() || 'pending',
        status: invitation.status,
        invited_by: null,
        created_at: new Date().toISOString(),
        accepted_at: null,
        last_active_at: null,
        expires_at: invitation.expires_at,
        is_pending: true,
        is_expired: false,
      };

      state.members.push(newMember);
    });
    builder.addCase(inviteMember.rejected, (state, action) => {
      state.inviteLoading = false;
      state.inviteError = action.error.message || 'Failed to invite member';
    });

    // Remove member
    builder.addCase(removeMember.fulfilled, (state, action) => {
      const memberId = action.meta.arg.memberId;
      state.members = state.members.filter((member) => String(member.id) !== String(memberId));
    });

    // Resend invitation
    builder.addCase(resendInvitation.pending, (state) => {
      // Could add loading state if needed
    });
    builder.addCase(resendInvitation.fulfilled, (state) => {
      // No state update needed, just a success notification
    });

    // Delete pending invitation
    builder.addCase(deletePendingInvitation.fulfilled, (state, action) => {
      const invitationId = action.meta.arg.invitationId;
      state.members = state.members.filter((member) => String(member.id) !== String(invitationId));
    });

    // Fetch organization by ID
    builder.addCase(fetchOrganizationById.pending, (state) => {
      state.fetchOrgByIdLoading = true;
      state.fetchOrgByIdError = null;
    });
    builder.addCase(fetchOrganizationById.fulfilled, (state, action) => {
      state.fetchOrgByIdLoading = false;
      // Store in eventHostOrganization to keep it separate from user's own organization
      state.eventHostOrganization = action.payload.data?.organization || action.payload.data;
    });
    builder.addCase(fetchOrganizationById.rejected, (state, action) => {
      state.fetchOrgByIdLoading = false;
      state.fetchOrgByIdError = action.error.message || 'Failed to fetch organization';
    });
  },
});

export const hostOrganizationActions = hostOrganizationSlice.actions;
